/*
 * /patient (list page)
 */
import {
	Button,
	Card,
	CardContent,
	Container,
	Input,
	Page,
	PageHeader,
	Placeholder,
} from "@emr/react-ui"
import { cn } from "@emr/react-ui"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ChevronRight, Plus, User } from "lucide-react"
import { useState } from "react"
import { z } from "zod"
import { trpc } from "../../trpc"
import type { ClientStatus } from "@emr/db/client"

const patientSearchSchema = z.object({
	status: z.string().optional(),
})

export const Route = createFileRoute("/patient/")({
	validateSearch: patientSearchSchema,
	component: PatientListPage,
})

const STATUS_LABELS: Record<string, string> = {
	all: "All Patients",
	none: "No Status",
	review_required: "Review Required",
	education_required: "Education Required",
	education_scheduled: "Education Scheduled",
	prescription_required: "Prescription Required",
	prescription_scheduled: "Prescription Scheduled",
	active: "Active",
	sla_required: "SLA Required",
	sla_scheduled: "SLA Scheduled",
	renewal_required: "Renewal Required",
	not_active: "Not Active",
}

const STATUS_COLORS: Record<ClientStatus, string> = {
	review_required: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
	education_required: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
	education_scheduled: "bg-blue-200 text-blue-800 dark:bg-blue-800/40 dark:text-blue-200",
	prescription_required: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
	prescription_scheduled: "bg-amber-200 text-amber-800 dark:bg-amber-800/40 dark:text-amber-200",
	active: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
	sla_required: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
	sla_scheduled: "bg-orange-200 text-orange-800 dark:bg-orange-800/40 dark:text-orange-200",
	renewal_required: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
	not_active: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
}

function PatientListPage() {
	const navigate = useNavigate()
	const { status: rawStatus } = Route.useSearch()
	const activeStatus = rawStatus ?? "all"
	const [search, setSearch] = useState("")

	const { data: patients, isLoading } = trpc.patient.getAllByClientStatus.useQuery({
		status: (["all", "none", ...Object.keys(STATUS_LABELS)].includes(activeStatus)
			? activeStatus
			: "all") as any,
	})

	const filtered = patients?.filter((p) => {
		const q = search.toLowerCase()
		if (!q) return true
		return (
			p.firstName.toLowerCase().includes(q) ||
			p.lastName.toLowerCase().includes(q)
		)
	})

	const pageTitle = STATUS_LABELS[activeStatus] ?? "Patients"

	return (
		<Page>
			<Container width="xl" padding="lg" gap="lg">
				<PageHeader
					title={pageTitle}
					right={{
						Component: (
							<Button onClick={() => navigate({ to: "/patient/new" })}>
								<Plus className="w-4 h-4 mr-1" />
								New Patient
							</Button>
						),
					}}
				/>

				<Input
					placeholder="Search by name..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					className="max-w-sm"
				/>

				{isLoading ? (
					<Placeholder title="Loading..." />
				) : !filtered || filtered.length === 0 ? (
					<Placeholder title={search ? "No patients match your search." : "No patients yet."} />
				) : (
					<div className="flex flex-col gap-3">
						{filtered.map((patient) => (
							<Link
								key={patient.id}
								to="/patient/$patientId"
								params={{ patientId: patient.id }}
								className="block"
							>
								<Card className="hover:bg-accent/50 transition-colors cursor-pointer">
									<CardContent className="flex items-center justify-between py-4">
										<div className="flex items-center gap-3">
											<User className="w-5 h-5 text-muted-foreground shrink-0" />
											<div>
												<p className="font-medium">
													{patient.firstName} {patient.lastName}
												</p>
												{patient.dateOfBirth && (
													<p className="text-sm text-muted-foreground">
														DOB:{" "}
														{new Date(patient.dateOfBirth).toLocaleDateString(
															"en-CA",
															{ year: "numeric", month: "short", day: "numeric" },
														)}
													</p>
												)}
											</div>
										</div>
										<div className="flex items-center gap-3">
											{(patient as any).clientStatus && (
												<StatusPill status={(patient as any).clientStatus} />
											)}
											<ChevronRight className="w-4 h-4 text-muted-foreground" />
										</div>
									</CardContent>
								</Card>
							</Link>
						))}
					</div>
				)}
			</Container>
		</Page>
	)
}

function StatusPill({ status }: { status: ClientStatus }) {
	return (
		<span
			className={cn(
				"inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
				STATUS_COLORS[status] ?? "bg-muted text-muted-foreground",
			)}
		>
			{STATUS_LABELS[status] ?? status}
		</span>
	)
}
